import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { BomImage } from "./BomImage";
import { printPurchaseList } from "./purchaseList";
import { fmtQty } from "@/lib/constants";
import { ShoppingCart, Printer, PackageCheck, AlertTriangle } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function PurchaseSuggestionsDialog({ open, onOpenChange, suggestions, onCreatePO, openPoItemIds, suppliers }) {
  const list = suggestions || [];
  const supObjs = suppliers || [];
  const supNames = supObjs.map((s) => s.name);
  const [chosen, setChosen] = useState({});
  const hasPo = (s) => openPoItemIds && openPoItemIds.has(s.item_id);
  const matchingFor = (row) => {
    const cat = String(row.category || "").toLowerCase().trim();
    if (!cat) return [];
    return supObjs
      .filter((sup) => (sup.categories || []).some((c) => {
        const cc = String(c).toLowerCase().trim();
        return cc === cat || cc.includes(cat) || cat.includes(cc);
      }))
      .map((sup) => sup.name);
  };
  const defaultSupplier = (row, matching) => (supNames.includes(row.supplier) ? row.supplier : (matching[0] || ""));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="purchase-suggestions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-blue-600" />
            Purchase Suggestions
          </DialogTitle>
          <DialogDescription className="text-xs">
            Consolidated across all open orders. Order qty covers demand and restores each material's reorder-level buffer.
          </DialogDescription>
        </DialogHeader>

        {list.length === 0 ? (
          <div className="mt-6 flex flex-col items-center gap-2 text-center py-8" data-testid="purchase-empty-state">
            <PackageCheck className="h-8 w-8 text-emerald-400" />
            <p className="text-sm text-slate-600 font-medium">Nothing to Reorder</p>
            <p className="text-xs text-slate-400 max-w-[320px]">
              Current stock covers all open-order demand and every reorder-level buffer.
            </p>
          </div>
        ) : (
          <div className="mt-2 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                <span className="font-semibold text-slate-800">{list.length}</span> material(s) to reorder
              </p>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => printPurchaseList(list)} data-testid="print-purchase-list-btn">
                <Printer className="h-3.5 w-3.5 mr-1" /> Print Purchase List
              </Button>
            </div>
            <div className="rounded-lg border border-slate-200 overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2">Material</th>
                    <th className="text-left font-semibold px-3 py-2">Supplier</th>
                    <th className="text-right font-semibold px-3 py-2">Lead</th>
                    <th className="text-right font-semibold px-3 py-2">Needed</th>
                    <th className="text-right font-semibold px-3 py-2">In Stock</th>
                    <th className="text-right font-semibold px-3 py-2">Order Qty</th>
                    <th className="text-right font-semibold px-3 py-2">Latest Order Date</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.map((s) => {
                    const matching = matchingFor(s);
                    const picked = chosen[s.sku] ?? defaultSupplier(s, matching);
                    const ordered = [...matching, ...supNames.filter((n) => !matching.includes(n))];
                    return (
                    <tr key={s.sku} data-testid={`purchase-row-${s.sku}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <BomImage src={s.image_url} alt={s.name} className="h-8 w-8" />
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                            <p className="text-[10px] text-slate-400 font-mono-data">{s.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {hasPo(s) ? (
                          s.supplier || <span className="text-slate-300">—</span>
                        ) : (
                          <div className="space-y-0.5">
                          <SearchableSelect
                            value={picked}
                            onValueChange={(v) => setChosen((p) => ({ ...p, [s.sku]: v }))}
                            options={[
                              ...(s.supplier && !supNames.includes(s.supplier) ? [{ value: s.supplier, label: `${s.supplier} (default)`, keywords: s.supplier }] : []),
                              ...ordered.map((n) => ({ value: n, label: `${n}${matching.includes(n) ? " · match" : ""}`, keywords: n })),
                            ]}
                            placeholder="Select supplier…"
                            searchPlaceholder="Search suppliers…"
                            emptyText="No suppliers registered."
                            className="max-w-[190px]"
                            triggerClassName="h-7 text-[11px] px-2"
                            testid={`supplier-select-${s.sku}`}
                          />
                          {s.category && matching.length > 0 && (
                            <p className="text-[9px] text-emerald-600" data-testid={`supplier-match-${s.sku}`}>✓ {matching.length} match “{s.category}”</p>
                          )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-600 whitespace-nowrap">{s.lead_time_days ? `${s.lead_time_days}d` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-600 whitespace-nowrap">{fmtQty(s.demand, s.unit)} {s.unit}</td>
                      <td className="px-3 py-2 text-right font-mono-data text-slate-600 whitespace-nowrap">{fmtQty(s.in_stock, s.unit)} {s.unit}</td>
                      <td className="px-3 py-2 text-right font-mono-data font-bold text-blue-700 whitespace-nowrap" data-testid={`purchase-qty-${s.sku}`}>
                        {fmtQty(s.suggested_qty, s.unit)} {s.unit}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono-data whitespace-nowrap ${s.overdue ? "text-red-600 font-bold" : "text-slate-600"}`} data-testid={`purchase-latest-date-${s.sku}`}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          {s.overdue && <AlertTriangle className="h-3 w-3" data-testid={`purchase-overdue-${s.sku}`} />}
                          {fmtDate(s.latest_order_date)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {onCreatePO && (
                          hasPo(s) ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid={`po-exists-${s.sku}`}>
                              <PackageCheck className="h-3.5 w-3.5" /> PO raised
                            </span>
                          ) : (
                            <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" disabled={!picked} onClick={() => onCreatePO(s, picked)} data-testid={`create-po-${s.sku}`}>
                              Create PO
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
